import React from 'react';
import { FacebookIcon } from '@components/IconComponents/SocialIcons/FacebookIcon';
import { LinkedInIcon } from '@components/IconComponents/SocialIcons/LinkedInIcon';
import { MailIcon, v2Colors } from '@web-for-marketing/react-ui';
import { XIcon } from '@components/IconComponents/SocialIcons/XIcon';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { getTranslation } from '@helpers/languageTranslations';
import { useTranslation } from '@stateManagement/TranslationContext';

const classes = {
    mailIcon: {
        '& path': {
            fill: 'currentColor',
        },
    },
    roundedShareBorder: {
        margin: '0 0.5rem',
        position: 'relative',
        border: `1px solid ${v2Colors.border.activePrimary}`,
        width: '4rem',
        height: '4rem',
        borderRadius: '50%',
        background: v2Colors.core.snow,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textDecoration: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
        color: v2Colors.border.activePrimary,
        '& svg': {
            color: v2Colors.border.activePrimary,
        },
        '&:hover': {
            cursor: 'pointer',
            border: `1px solid ${v2Colors.core.snow}`,
            color: v2Colors.core.snow,
            backgroundColor: v2Colors.border.activePrimary,
            '& svg': {
                strokeWidth: 1.1,
                color: `${v2Colors.core.snow} !important`,
                backgroundColor: `${v2Colors.border.activePrimary} !important`,
            },
        },
        '&:focus-visible': {
            backgroundColor: `${v2Colors.border.activePrimary} !important`,
            color: v2Colors.core.snow,
            outline: 'none',
            '& svg': {
                strokeWidth: 1.1,
                color: `${v2Colors.core.snow} !important`,
                backgroundColor: `${v2Colors.border.activePrimary} !important`,
            },
        },
        '&:active': {
            backgroundColor: `${v2Colors.border.activePrimary} !important`,
            color: v2Colors.core.snow,
            outline: 'none',
            '& svg': {
                strokeWidth: 1.1,
                color: `${v2Colors.core.snow} !important`,
                backgroundColor: `${v2Colors.border.activePrimary} !important`,
            },
        },
    },
} as const;

function socialPopUpWindow(link: string | URL, socialType: string): void {
    const featureStyle = 'width=550,height=600,left=150,top=200,toolbar=0,status=0,';
    window.open(link, socialType, featureStyle);
}

interface ShareSocialMediaButtonsProps {
    className?: string;
}

export function ShareSocialMediaButtons({ className }: ShareSocialMediaButtonsProps): JSX.Element {
    const { fullUrl } = useStaticValues();
    const { selectedLanguageTranslation } = useTranslation();

    const socialPlatforms = [
        {
            socialType: 'facebook',
            link: `https://www.facebook.com/sharer/sharer.php?u=${fullUrl}&amp;src=sdkpreparse`,
            icon: <FacebookIcon />,
        },
        {
            socialType: 'twitter',
            link: `https://twitter.com/intent/tweet?url=${fullUrl}`,
            icon: <XIcon css={classes.mailIcon} />,
        },
        {
            socialType: 'linkedIn',
            link: `https://www.linkedin.com/sharing/share-offsite/?url=${fullUrl}`,
            icon: <LinkedInIcon />,
        },
        {
            socialType: 'mail',
            link: `mailto:?to=&subject=Sharing an article from Geotab&body=${fullUrl}`,
            icon: <MailIcon css={classes.mailIcon} />,
        },
    ];

    return (
        <div css={{ display: 'flex' }} className={className}>
            {socialPlatforms.map(({ socialType, link, icon }, index) => (
                <button
                    css={classes.roundedShareBorder}
                    key={`Social ${index}`}
                    tabIndex={0}
                    onClick={() => socialPopUpWindow(link, socialType)}
                    aria-label={`${getTranslation(selectedLanguageTranslation, 'blogPost', 'shareWith')} ${socialType}`}
                >
                    {icon}
                </button>
            ))}
        </div>
    );
}
